/**
 * A scan result, held as text so it can be corrected before it is trusted.
 *
 * `ARCHITECTURE.md` is explicit that a label read "is a draft to check, never a
 * measurement" and that "every read value is shown editable". The web app did
 * that; the phone showed the same four figures as static text, which is the
 * client where nearly all scanning happens.
 *
 * Held as strings rather than numbers because a half-typed "1" is not a 1 kcal
 * food — parsing on every keystroke turns backspacing "120" into 12, then 1,
 * then NaN, and a controlled field fights the user the whole way.
 */

import { figureChange, seedFigure } from "./figures";
import { saltG, sodiumMg } from "./nutrients";
import { parseDecimal } from "./parse";
import type { FoodItem } from "./scan";

/**
 * The figures a reader can get wrong, plus the name.
 *
 * The optional four are optional on the type as well as on the packet, so a
 * client that does not offer boxes for them compiles and behaves exactly as
 * before. Salt is the one that is not stored in the unit it is typed in — see
 * `SALT` below.
 */
export interface Reading {
  name: string;
  kcal: string;
  protein: string;
  carbs: string;
  fat: string;
  saturates?: string;
  fiber?: string;
  sugar?: string;
  /** Grams of salt, as printed. Stored as sodium in milligrams. */
  salt?: string;
}

const round = (n: number) => Math.round(n * 10) / 10;

/** Salt is typed in grams and stored as sodium in milligrams. */
const SALT = { decimals: 1, display: saltG, store: sodiumMg };

export function toReading(food: FoodItem): Reading {
  return {
    name: food.name,
    kcal: String(round(food.kcal_per_100)),
    protein: String(round(food.protein_g_per_100)),
    carbs: String(round(food.carbs_g_per_100)),
    fat: String(round(food.fat_g_per_100)),
    saturates: seedFigure(food.saturated_fat_g_per_100),
    fiber: seedFigure(food.fiber_g_per_100),
    sugar: seedFigure(food.sugar_g_per_100),
    salt: seedFigure(food.sodium_mg_per_100, SALT),
  };
}

const FIELDS = [
  ["kcal", "kcal_per_100"],
  ["protein", "protein_g_per_100"],
  ["carbs", "carbs_g_per_100"],
  ["fat", "fat_g_per_100"],
] as const;

/**
 * The optional four, which need `figureChange` rather than the loop below —
 * salt is compared in the unit it was typed in, and comparing 1.2 g against a
 * stored 483 mg would file a correction on every save.
 */
const OPTIONAL = [
  ["saturates", "saturated_fat_g_per_100", {}],
  ["fiber", "fiber_g_per_100", {}],
  ["sugar", "sugar_g_per_100", {}],
  ["salt", "sodium_mg_per_100", SALT],
] as const;

/**
 * Only what actually changed.
 *
 * Sending every field back would mark an untouched food as corrected, which on
 * a barcode row is an edit other people inherit — and the audit trail in
 * `food_corrections` would say somebody reviewed numbers they never looked at.
 *
 * Comparison is at one decimal place, the same precision the fields are seeded
 * at. Without that, seeding "12.3" from a stored 12.34 and saving without
 * touching anything would file a correction for a rounding difference.
 */
export function diffReading(food: FoodItem, edit: Reading): Partial<FoodItem> {
  const changes: Partial<FoodItem> = {};

  const name = edit.name.trim();
  if (name && name !== food.name) changes.name = name;

  for (const [typed, field] of FIELDS) {
    const value = parseDecimal(edit[typed]);
    // A blank or malformed field is left alone rather than treated as zero.
    // "I cleared it because I don't know" must not overwrite a read value with
    // a confident nought.
    if (!Number.isFinite(value) || value < 0) continue;
    if (round(value) !== round(food[field])) changes[field] = value;
  }

  for (const [typed, field, spec] of OPTIONAL) {
    const change = figureChange(edit[typed], food[field], spec);
    // `set` only, never `cleared`. This form is held one-handed at a shelf, and
    // taking a figure away is a deliberate act that belongs in the full editor
    // — the same rule the macros above follow, for the same reason.
    if (change.state === "set") changes[field] = change.value;
  }

  return changes;
}
